import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { translationChunksConfig, translationsEn } from '@spartacus/assets';
import { CurrencyService, HttpErrorHandler, I18nConfig, LanguageService, provideConfig } from '@spartacus/core';
import { PageSlotModule } from '@spartacus/storefront';
import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { AuthErrorInterceptor } from 'src/app/common/interceptors/auth-error.interceptor';
import { CartRequestInterceptor } from 'src/app/common/interceptors/cart-expiration.interceptor';
import { KnBrSalesAreaInterceptor } from 'src/app/common/interceptors/kn-br-sales-area.interceptor';
import { KnBrSalesOrgErrorHandler } from 'src/app/common/interceptors/kn-br-sales-org-error.handler';
import { KnBrOutletModule } from 'src/app/common/kn-br-outlet/kn-br-outlet.module';
import { UserAccountService } from 'src/feature-libs/kn-br-user/kn-br-account/core/facade';
import { KnBrDateFormatService } from 'src/shared/kn-br-date-format.service';
import { KnBrCustomerContextAdapter } from 'src/store/kn-br-customer-context/kn-br-customer-context.adapter';
import { KnBrLayoutConfigModule } from './common/kn-br-layout-config/kn-br-layout-config.module';
import { SpartacusModule } from './spartacus/spartacus.module';
import { AppRoutingModule } from "@spartacus/storefront";

function currencyInitializerFactory(
  adapter: KnBrCustomerContextAdapter,
  currencyService: CurrencyService
): () => void {
  return () => {
    const currency = adapter.getCurrency();
    if (currency) {
      currencyService.setActive(currency);
    }
  };
}

// Mirrors dateFormatInitializerFactory below: waits for the current-user call so the
// user's saved language (user.language, e.g. { active: true, isocode: 'fr_CA', ... })
// can override the config/localStorage default before the storefront renders.
function languageInitializerFactory(
  userAccountService: UserAccountService,
  languageService: LanguageService
): () => Promise<void> {
  return () =>
    firstValueFrom(
      userAccountService.get().pipe(
        filter((user: any) => !!user),
        take(1),
        timeout(5000)
      )
    )
      .then((user: any) => {
        if (user?.language?.isocode && user.language.active !== false) {
          languageService.setActive(user.language.isocode);
        }
      })
      .catch(() => {
        /* guest session or timed out -- keep the default/stored language */
      });
}

// Blocks bootstrap briefly so the API's dateFormat is cached before any component
// renders a date -- without this, components that patch a date on their own early
// data load (before the current-user call resolves elsewhere) render using the
// default format and never re-render once the real format arrives. Bounded by
// timeout() so a guest/unauthenticated session (where the current-user call never
// resolves) can't hang app startup.
function dateFormatInitializerFactory(
  userAccountService: UserAccountService,
  dateFormatService: KnBrDateFormatService
): () => Promise<void> {
  return () =>
    firstValueFrom(
      userAccountService.get().pipe(
        filter((user: any) => !!user),
        take(1),
        timeout(5000)
      )
    )
      .then((user: any) => {
        if (user?.dateFormat) {
          dateFormatService.setDateFormat(user.dateFormat);
        }
      })
      .catch(() => {
        /* guest session or timed out -- keep the default format */
      });
}


@NgModule({
  imports: [KnBrOutletModule, PageSlotModule, SpartacusModule, MatSidenavModule, KnBrLayoutConfigModule, AppRoutingModule],
  providers: [
    provideConfig({
      features: {
        a11yImproveContrast: false,
      },
    }),
    provideConfig(<I18nConfig>{
      // we bring in static translations to be up and running soon right away
      i18n: {
        resources: {
          en: translationsEn,
        },
        chunks: translationChunksConfig,
        fallbackLang: 'en',
      },
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CartRequestInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: KnBrSalesAreaInterceptor,
      multi: true,
    },
    {
      provide: HttpErrorHandler,
      useExisting: KnBrSalesOrgErrorHandler,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: currencyInitializerFactory,
      deps: [KnBrCustomerContextAdapter, CurrencyService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: dateFormatInitializerFactory,
      deps: [UserAccountService, KnBrDateFormatService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: languageInitializerFactory,
      deps: [UserAccountService, LanguageService],
      multi: true,
    },
  ],
})
export class AppModule {}
